// src/components/admin/DoctorEdit.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';

const DoctorEdit = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form fields
  const [formData, setFormData] = useState({
  // Personal Information
  firstName: '',
  lastName: '',
  email: '',
  personalContactNumber: '',
  dateOfBirth: '',
  gender: '',
  clinicLocation: '',
  clinicContactNumber: '',
  
  // Professional Information
  specialty: '',
  title: '',
  areasOfExpertise: '',
  experience: '',
  licenseNumber: '',
  licenseExpiryDate: '',
  licenseIssuingAuthority: '',
  // REMOVED: bio
  
  // Service Options
  inPerson: false,
  telehealth: false,
  
  // Additional
  // REMOVED: languages
  consultationFee: '',
  accountStatus: 'active',
  
  // Education and Certifications
  education: [],
  certifications: []
});

  useEffect(() => {
    fetchDoctorDetails();
  }, [doctorId]);

  const fetchDoctorDetails = async () => {
  try {
    setLoading(true);
    const response = await adminService.getDoctorDetails(doctorId);
    
    // The response is already response.data from adminService
    // So we need to extract the actual doctor object
    const doctorData = response.data || response;
    
    console.log('DOCTOR DATA EXTRACTED:', doctorData);
    
    if (!doctorData) {
      throw new Error('No doctor data received');
    }
    
    setDoctor(doctorData);
    
    // Map to match RegistrationTab field names EXACTLY
    setFormData({
  // Personal Information
  firstName: doctorData.firstName || '',
  lastName: doctorData.lastName || '',
  email: doctorData.email || '',
  personalContactNumber: doctorData.personalContactNumber || doctorData.phoneNumber || '',
  dateOfBirth: doctorData.dateOfBirth ? doctorData.dateOfBirth.split('T')[0] : '',
  gender: doctorData.gender || '',
  clinicLocation: doctorData.clinicAddress || doctorData.clinicLocation || '',
  clinicContactNumber: doctorData.clinicContactNumber || '',
  
  // Professional Information
  specialty: doctorData.specialty || doctorData.specialization || '',
  title: doctorData.title || '',
  areasOfExpertise: Array.isArray(doctorData.areasOfExpertise) 
    ? doctorData.areasOfExpertise.join(', ') 
    : (doctorData.areasOfExpertise || ''),
  experience: doctorData.experience || doctorData.yearsOfExperience || doctorData.yearsOfPractice || '',
  licenseNumber: doctorData.licenseNumber || '',
  licenseExpiryDate: doctorData.licenseExpiryDate ? doctorData.licenseExpiryDate.split('T')[0] : '',
  licenseIssuingAuthority: doctorData.licenseIssuingAuthority || '',
  
  // Service Options
  inPerson: doctorData.inPerson || doctorData.appointmentTypes?.inPerson || false,
  telehealth: doctorData.telehealth || doctorData.appointmentTypes?.telehealth || false,
  
  // Additional
  consultationFee: doctorData.consultationFee || '',
  accountStatus: doctorData.accountStatus || 'active',
  
  // Education and Certifications
  education: doctorData.education || [],
  certifications: doctorData.certifications || []
});
    
  } catch (err) {
    console.error('Error fetching doctor details:', err);
    setError('Failed to load doctor details.');
  } finally {
    setLoading(false);
  }
};

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  
  try {
    const dataToSend = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      personalContactNumber: formData.personalContactNumber,
      clinicLocation: formData.clinicLocation,
      clinicContactNumber: formData.clinicContactNumber,
      specialty: formData.specialty,
      title: formData.title,
      areasOfExpertise: formData.areasOfExpertise || '', // ✅ Keep as string
      experience: formData.experience ? Number(formData.experience) : 0,
      inPerson: formData.inPerson,
      telehealth: formData.telehealth,
      consultationFee: formData.consultationFee ? Number(formData.consultationFee) : 0,
      accountStatus: formData.accountStatus,
      education: formData.education,
      certifications: formData.certifications
    };
    
    // Only include optional fields if they have values
    if (formData.dateOfBirth) dataToSend.dateOfBirth = formData.dateOfBirth;
    if (formData.gender) dataToSend.gender = formData.gender;
    if (formData.licenseNumber) dataToSend.licenseNumber = formData.licenseNumber;
    if (formData.licenseExpiryDate) dataToSend.licenseExpiryDate = formData.licenseExpiryDate;
    if (formData.licenseIssuingAuthority) dataToSend.licenseIssuingAuthority = formData.licenseIssuingAuthority;
    
    const tenantId = doctor?.tenant || doctor?.tenantId;
    
    await adminService.updateDoctor(doctorId, dataToSend, tenantId);
    toast.success('Doctor updated successfully!');
    navigate('/admin/professionals');
  } catch (err) {
    console.error('Error updating doctor:', err);
    toast.error(err.response?.data?.message || 'Failed to update doctor. Please try again.');
  } finally {
    setSaving(false);
  }
};

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading doctor details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin/professionals')}>
            Back to Professionals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Header */}
      <div className="page-header">
        <button 
          className="back-button"
          onClick={() => navigate('/admin/professionals')}
        >
          ← Back to Professionals
        </button>
        <div className="header-info">
          <h1>Edit Doctor Profile</h1>
          <p>Update {doctor?.firstName} {doctor?.lastName}'s information</p>
        </div>
      </div>

      {/* Edit Form */}
      <div className="form-container">
        <form onSubmit={handleSubmit} className="doctor-edit-form">
          {/* Personal Information */}
         /* Personal Information Section - COMPLETE */
<div className="form-section">
  <h3>Personal Information</h3>
  <div className="form-grid">
    <div className="form-group">
      <label htmlFor="firstName">First Name *</label>
      <input
        type="text"
        id="firstName"
        name="firstName"
        value={formData.firstName}
        onChange={handleInputChange}
        required
      />
    </div>
    
    <div className="form-group">
      <label htmlFor="lastName">Last Name *</label>
      <input
        type="text"
        id="lastName"
        name="lastName"
        value={formData.lastName}
        onChange={handleInputChange}
        required
      />
    </div>
    
    <div className="form-group">
      <label htmlFor="email">Email Address *</label>
      <input
        type="email"
        id="email"
        name="email"
        value={formData.email}
        onChange={handleInputChange}
        required
      />
    </div>
    
    <div className="form-group">
      <label htmlFor="personalContactNumber">Personal Contact</label>
      <input
        type="tel"
        id="personalContactNumber"
        name="personalContactNumber"
        value={formData.personalContactNumber}
        onChange={handleInputChange}
      />
    </div>

    <div className="form-group">
      <label htmlFor="dateOfBirth">Date of Birth</label>
      <input
        type="date"
        id="dateOfBirth"
        name="dateOfBirth"
        value={formData.dateOfBirth}
        onChange={handleInputChange}
      />
    </div>

    <div className="form-group">
      <label htmlFor="gender">Gender</label>
      <select
        id="gender"
        name="gender"
        value={formData.gender}
        onChange={handleInputChange}
      >
        <option value="">Select Gender</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="Other">Other</option>
        <option value="Prefer not to say">Prefer not to say</option>
      </select>
    </div>
  </div>
</div>

{/* Professional Information Section - COMPLETE */}
<div className="form-section">
  <h3>Professional Information</h3>
  <div className="form-grid">
    <div className="form-group">
      <label htmlFor="specialty">Specialty *</label>
      <select
        id="specialty"
        name="specialty"
        value={formData.specialty}
        onChange={handleInputChange}
        required
      >
        <option value="">Select Specialty</option>
        <option value="Psychiatrist">Psychiatrist</option>
        <option value="Psychologist">Psychologist</option>
        <option value="Mental Health Counselor">Mental Health Counselor</option>
        <option value="Clinical Social Worker">Clinical Social Worker</option>
        <option value="Marriage and Family Therapist">Marriage and Family Therapist</option>
        <option value="Other">Other</option>
      </select>
    </div>
    
    <div className="form-group">
      <label htmlFor="title">Professional Title</label>
      <input
        type="text"
        id="title"
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        placeholder="Dr., MD, PhD, etc."
      />
    </div>

    <div className="form-group">
      <label htmlFor="areasOfExpertise">Areas of Expertise</label>
      <input
        type="text"
        id="areasOfExpertise"
        name="areasOfExpertise"
        value={formData.areasOfExpertise}
        onChange={handleInputChange}
        placeholder="e.g., Anxiety, Depression, PTSD"
      />
      <small className="form-hint">Comma-separated list</small>
    </div>
    
    <div className="form-group">
      <label htmlFor="experience">Years of Experience</label>
      <input
        type="number"
        id="experience"
        name="experience"
        value={formData.experience}
        onChange={handleInputChange}
        min="0"
      />
    </div>
    
    <div className="form-group">
      <label htmlFor="consultationFee">Consultation Fee</label>
      <input
        type="number"
        id="consultationFee"
        name="consultationFee"
        value={formData.consultationFee}
        onChange={handleInputChange}
        placeholder="Amount in USD"
        min="0"
        step="0.01"
      />
    </div>
  </div>
</div>

          {/* Clinic Information */}
          <div className="form-section">
            <h3>Clinic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="clinicLocation">Clinic Location</label>
                <input
                  type="text"
                  id="clinicLocation"
                  name="clinicLocation"
                  value={formData.clinicLocation}
                  onChange={handleInputChange}
                  placeholder="Clinic address or location"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="clinicContactNumber">Clinic Contact</label>
                <input
                  type="tel"
                  id="clinicContactNumber"
                  name="clinicContactNumber"
                  value={formData.clinicContactNumber}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Service Options */}
          <div className="form-section">
            <h3>Service Options</h3>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="inPerson"
                  name="inPerson"
                  checked={formData.inPerson}
                  onChange={handleInputChange}
                />
                <label htmlFor="inPerson">Offers In-Person Consultations</label>
              </div>
              
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="telehealth"
                  name="telehealth"
                  checked={formData.telehealth}
                  onChange={handleInputChange}
                />
                <label htmlFor="telehealth">Offers Telehealth Consultations</label>
              </div>
            </div>
          </div>

          {/* License Information */}
          <div className="form-section">
            <h3>License Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="licenseNumber">License Number</label>
                <input
                  type="text"
                  id="licenseNumber"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="licenseIssuingAuthority">Issuing Authority</label>
                <input
                  type="text"
                  id="licenseIssuingAuthority"
                  name="licenseIssuingAuthority"
                  value={formData.licenseIssuingAuthority}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="licenseExpiryDate">License Expiry Date</label>
                <input
                  type="date"
                  id="licenseExpiryDate"
                  name="licenseExpiryDate"
                  value={formData.licenseExpiryDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Account Status */}
          <div className="form-section">
            <h3>Account Status</h3>
            <div className="form-group">
              <label htmlFor="accountStatus">Status</label>
              <select
                id="accountStatus"
                name="accountStatus"
                value={formData.accountStatus}
                onChange={handleInputChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => navigate('/admin/professionals')}
              disabled={saving}
            >
              Cancel
            </button>
            
            <button 
              type="submit" 
              className="btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorEdit;